import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Form, FormField, FormItem, FormControl, FormMessage, FormLabel, FormDescription } from 'components/ui/form';
import { Checkbox } from 'components/ui/checkbox';
import { NavArrowLeft, Check, Xmark } from 'iconoir-react';
import { cn } from 'lib/utils';
import { WarningAlert } from 'components/blocks/warning-alert/warning-alert';
import {
    useSubscribePlan,
    useGetBillingProfile,
    useUpdateBillingProfile,
    usePreviewSubscriptionDiscount,
} from 'features/subscription/hooks/use-subscription';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import {
    buildUpdateBillingProfileRequest,
    type BillingProfileFormValues,
    type DiscountPreviewResult,
} from 'features/subscription/services/subscription-service';

export interface ApiPlanPrice {
    priceId: string;
    currencyCode: string;
    unitAmountMinor: number;
    interval: string;
    intervalCount: number;
    quantityItemKey: string | null;
}

export interface ApiPlanMeterTier {
    upToQuantity: number | null;
    unitAmountMinor: number;
}

export interface ApiPlanMeterRateTable {
    currencyCode: string;
    tiers: ApiPlanMeterTier[];
}

export interface ApiPlanMeter {
    meterKey: string;
    displayName: string;
    unitLabel: string;
    aggregation: string;
    includedQuantity: number;
    overageAllowed: boolean;
    thresholdPercents: number[];
    rateTables: ApiPlanMeterRateTable[];
}

export interface ApiPlanEntitlement {
    key: string;
    limitKind: 'Boolean' | 'Count' | 'Unlimited';
    limit: number | null;
    meterKey: string | null;
    unitLabel: string | null;
}

export interface ApiPlanTrialGrant {
    meterKey: string;
    includedQuantity: number;
}

export interface ApiPlanQuantityItem {
    itemKey: string;
    unitLabel: string;
    minQuantity: number;
    maxQuantity: number | null;
    defaultQuantity: number;
}

export interface ApiPlan {
    planId: string;
    code: string;
    displayName: string;
    description: string | null;
    familyCode: string;
    familyRank: number;
    usageInterval: string;
    usageIntervalCount: number;
    quantityDiscountCombinationPolicy: string;
    organizationId: string | null;
    featuresJson: string | null;
    trialDays: number | null;
    trialRequiresPaymentMethod: boolean;
    version: number;
    hasSubscribers: boolean;
    prices: ApiPlanPrice[];
    entitlements: ApiPlanEntitlement[];
    meters: ApiPlanMeter[];
    quantityItems: ApiPlanQuantityItem[];
    trialGrants: ApiPlanTrialGrant[];
}

interface CheckoutPlanState {
    plan: ApiPlan;
    selectedPrice: ApiPlanPrice;
}

const klaxInputClassName =
    '!h-[54px] !min-h-[54px] !max-h-[54px] rounded-xl border-[#d1d1d1] px-5 !text-base font-normal leading-[1.6] text-black shadow-none placeholder:text-[#888888] focus-visible:ring-0 dark:border-[#3d3d3d] dark:bg-[#19191c] dark:text-true-white dark:placeholder:text-[#b0b0b0]';

const emptyBillingForm: BillingProfileFormValues = {
    legalName: '',
    displayName: '',
    billingContactName: '',
    billingContactEmail: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    countryCode: '',
    taxRegistrationId: '',
};

const hintClassName = '!mt-1.5 text-[13px] leading-[1.4] text-[#888888] dark:text-[#b0b0b0]';

const createBillingSchema = (t: (key: string, defaultValue?: string) => string) =>
    z.object({
        legalName: z.string().min(1, t('LEGAL_NAME_REQUIRED', 'Legal name is required')),
        displayName: z.string().optional(),
        billingContactName: z.string().min(1, t('NAME_REQUIRED')),
        billingContactEmail: z.string().min(1, t('EMAIL_REQUIRED')).email(t('VALID_EMAIL_REQUIRED')),
        line1: z.string().optional(),
        line2: z.string().optional(),
        city: z.string().optional(),
        region: z.string().optional(),
        postalCode: z.string().optional(),
        countryCode: z
            .string()
            .optional()
            .refine(
                (value) => !value || /^[A-Za-z]{2}$/.test(value),
                t('COUNTRY_CODE_INVALID', 'Two letters, ISO 3166-1.')
            ),
        taxRegistrationId: z.string().optional(),
    });

type BillingFormData = z.infer<ReturnType<typeof createBillingSchema>>;

export function CheckoutPlanPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const checkoutData = location.state as CheckoutPlanState;
    const didPrefill = useRef(false);

    const [seatQuantity, setSeatQuantity] = useState<number>(1);
    const [showMaxSeatWarning, setShowMaxSeatWarning] = useState(false);

    const [couponEnabled, setCouponEnabled] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; result: DiscountPreviewResult } | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);

    const { mutate: subscribePlan, isPending, data, isSuccess } = useSubscribePlan();
    const { data: billingProfile, isLoading: isBillingProfileLoading } = useGetBillingProfile();
    const { mutateAsync: updateBillingProfile, isPending: isUpdatingBillingProfile } =
        useUpdateBillingProfile();
    const { mutate: previewDiscount, isPending: isPreviewingDiscount } = usePreviewSubscriptionDiscount({
        onSuccess: (result) => {
            if (result.status === 'Applied') {
                setAppliedCoupon({ code: couponCode.trim(), result });
                setCouponError(null);
            } else {
                setAppliedCoupon(null);
                setCouponError(
                    result.message || t('COUPON_CODE_INVALID', 'This coupon code is not valid.')
                );
            }
        },
        onError: () => {
            setAppliedCoupon(null);
            setCouponError(t('COUPON_APPLY_FAILED', 'Could not apply coupon. Please try again.'));
        },
    });
    const user = useCommonStore((s) => s.user);
    const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
    const organizationId = selectedOrgId || user?.organizationId || '';
    const defaultEmail = user?.email ?? '';
    const defaultName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

    const billingSchema = createBillingSchema(t);
    const form = useForm<BillingFormData>({
        resolver: zodResolver(billingSchema),
        defaultValues: {
            ...emptyBillingForm,
            billingContactEmail: defaultEmail,
            billingContactName: defaultName,
        },
    });
    const { handleSubmit, control, trigger, watch, reset, getValues } = form;
    const watchedRequired = watch([
        'legalName',
        'billingContactName',
        'billingContactEmail',
    ]);
    const isBillingIncomplete = watchedRequired.some((value) => !value?.trim());

    useEffect(() => {
        if (isBillingProfileLoading || didPrefill.current) return;
        didPrefill.current = true;
        reset({
            legalName: billingProfile?.legalName ?? '',
            displayName: billingProfile?.displayName ?? '',
            billingContactName: billingProfile?.billingContactName || defaultName,
            billingContactEmail: billingProfile?.billingContactEmail || defaultEmail,
            line1: billingProfile?.address?.line1 ?? '',
            line2: billingProfile?.address?.line2 ?? '',
            city: billingProfile?.address?.city ?? '',
            region: billingProfile?.address?.region ?? '',
            postalCode: billingProfile?.address?.postalCode ?? '',
            countryCode: billingProfile?.address?.countryCode ?? '',
            taxRegistrationId: billingProfile?.taxRegistrationId ?? '',
        });
    }, [billingProfile, isBillingProfileLoading, defaultName, defaultEmail, reset]);

    useEffect(() => {
        if (!checkoutData) {
            navigate('/subscription-plans');
        }
    }, [checkoutData, navigate]);

    useEffect(() => {
        if (isSuccess && data) {
            const checkoutUrl = (data as any)?.data?.checkoutUrl;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            }
        }
    }, [isSuccess, data]);

    if (!checkoutData) {
        return null;
    }

    const { plan, selectedPrice } = checkoutData;
    const isTeamPlan = plan.code?.toLowerCase().includes('team');
    const seatItem = plan.quantityItems.find(
        (item) => item.itemKey === 'seat' || item.itemKey === 'user'
    );
    const minSeats = seatItem?.minQuantity ?? 1;
    const maxSeats = seatItem?.maxQuantity ?? seatItem?.defaultQuantity ?? 1;

    const unitPrice = selectedPrice.unitAmountMinor / 100;
    const basePrice = isTeamPlan ? unitPrice * seatQuantity : unitPrice;
    const subtotal = basePrice;

    const appliedQuote = appliedCoupon?.result.status === 'Applied' ? appliedCoupon.result.quote : null;
    const currencyCode = appliedQuote?.currencyCode || selectedPrice.currencyCode;
    const displaySubtotal = appliedQuote ? appliedQuote.subtotalMinor / 100 : subtotal;
    const discountAmount = appliedQuote ? appliedQuote.discountMinor / 100 : 0;
    const totalDue = appliedQuote ? appliedQuote.totalDueNowMinor / 100 : subtotal;

    const buildQuantities = (seatOverride?: number) =>
        isTeamPlan
            ? plan.quantityItems.map((item) => ({
                itemKey: item.itemKey,
                quantity: (item.itemKey === 'user' || item.itemKey === 'seat') ? (seatOverride ?? seatQuantity) : item.defaultQuantity,
            }))
            : undefined;

    const runDiscountPreview = (code: string, seatOverride?: number) => {
        const quantities = buildQuantities(seatOverride);
        previewDiscount({
            planCode: plan.code,
            priceId: selectedPrice.priceId,
            ...(quantities && quantities.length > 0 && { quantities }),
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            discountCode: code,
            organizationId: organizationId || '',
            billingEmail: getValues('billingContactEmail') || defaultEmail,
            billingName: getValues('billingContactName') || defaultName,
        });
    };

    const handleApplyCoupon = () => {
        const code = couponCode.trim();
        if (!code || isPreviewingDiscount) return;
        runDiscountPreview(code);
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponError(null);
        setCouponCode('');
    };

    const onSubmit = async (formData: BillingFormData) => {
        const isValid = await trigger();
        if (!isValid) {
            return;
        }

        const quantities = buildQuantities();

        const checkoutPayload = {
            planCode: plan.code,
            priceId: selectedPrice.priceId,
            ...(quantities && quantities.length > 0 && { quantities }),
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            discountCode: appliedQuote ? appliedCoupon?.code : null,
            billingEmail: formData.billingContactEmail,
            billingName: formData.billingContactName,
        };

        if (organizationId) {
            try {
                await updateBillingProfile(
                    buildUpdateBillingProfileRequest(organizationId, formData)
                );
            } catch {
                return;
            }
        }

        subscribePlan(checkoutPayload);
    };

    const handleBackToPlans = () => {
        navigate('/subscription-plans');
    };

    const getIntervalLabel = (interval: string, count: number) => {
        if (count === 1) {
            return t(`INTERVAL_${interval.toUpperCase()}`, interval);
        }
        return `${count} ${t(`INTERVAL_${interval.toUpperCase()}S`, `${interval}s`)}`;
    };

    const formatMoney = (amount: number) =>
        `${currencyCode} ${amount.toFixed(2)}`;

    const formatLabel = (key: string) => key.replace(/-/g, ' ');

    const isBusy = isPending || isUpdatingBillingProfile || isBillingProfileLoading;

    return (
        <div className="min-h-screen font-sans lg:grid lg:grid-cols-2">
            <aside className="flex items-start justify-center bg-[#faf9f5] px-6 py-8 dark:bg-background lg:px-10 lg:py-10">
                <div className="w-full max-w-[600px]">
                    <button
                        type="button"
                        onClick={handleBackToPlans}
                        className="mb-8 inline-flex items-center gap-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <NavArrowLeft width={20} height={20} strokeWidth={1.75} />
                        <span>{t('BACK_TO_PLANS', 'Back to Plans')}</span>
                    </button>

                    <h1 className="text-3xl font-bold leading-[1.2] text-[#171b23] dark:text-foreground">
                        {plan.displayName}
                    </h1>
                    {plan.description && (
                        <p className="mt-2 text-sm text-muted-foreground">
                            {plan.description}
                        </p>
                    )}

                    <h2 className="mb-6 mt-10 text-base font-semibold leading-[1.2] text-grey-700 dark:text-foreground">
                        {t('ORDER_SUMMARY', 'Order summary')}
                    </h2>

                    <div className="space-y-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="font-medium text-[#171b23] dark:text-foreground">{plan.displayName}</p>
                                <p className="mt-0.5 text-sm text-muted-foreground">
                                    {getIntervalLabel(selectedPrice.interval, selectedPrice.intervalCount)}
                                    {isTeamPlan ? ` · ${formatMoney(unitPrice)} / ${t('SEAT', 'seat')}` : ''}
                                </p>
                            </div>
                            <p className="shrink-0 font-medium text-[#171b23] dark:text-foreground">
                                {formatMoney(subtotal)}
                            </p>
                        </div>

                        {isTeamPlan && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-4">
                                    <Label htmlFor="seatQuantity" className="text-sm font-medium text-muted-foreground">
                                        {t('NUMBER_OF_SEATS', 'Number of seats')}
                                    </Label>
                                    <Input
                                        id="seatQuantity"
                                        type="number"
                                        min={minSeats}
                                        max={maxSeats}
                                        value={seatQuantity}
                                        onChange={(e) => {
                                            const parsed = parseInt(e.target.value, 10);
                                            let nextSeatQuantity = minSeats;
                                            if (Number.isNaN(parsed)) {
                                                nextSeatQuantity = minSeats;
                                                setShowMaxSeatWarning(false);
                                            } else if (parsed > maxSeats) {
                                                nextSeatQuantity = maxSeats;
                                                setShowMaxSeatWarning(true);
                                            } else {
                                                nextSeatQuantity = Math.max(minSeats, parsed);
                                                setShowMaxSeatWarning(false);
                                            }
                                            setSeatQuantity(nextSeatQuantity);
                                            if (appliedCoupon) {
                                                runDiscountPreview(appliedCoupon.code, nextSeatQuantity);
                                            }
                                        }}
                                        className="w-20 text-center"
                                    />
                                </div>
                                {showMaxSeatWarning && (
                                    <WarningAlert>
                                        {t(
                                            'MAX_SEATS_WARNING',
                                            'You can purchase a maximum of {{max}} seats.',
                                            { max: maxSeats }
                                        )}
                                    </WarningAlert>
                                )}
                            </div>
                        )}

                        <div className="space-y-2.5 border-t border-[#e7e7e7] pt-4 dark:border-border">
                            <div className="flex items-center gap-2.5">
                                <Checkbox
                                    id="hasCoupon"
                                    checked={couponEnabled}
                                    onCheckedChange={(checked) => {
                                        const isChecked = checked === true;
                                        setCouponEnabled(isChecked);
                                        if (!isChecked) {
                                            handleRemoveCoupon();
                                        }
                                    }}
                                />
                                <Label
                                    htmlFor="hasCoupon"
                                    className="cursor-pointer text-sm font-medium text-muted-foreground"
                                >
                                    {t('HAVE_COUPON_CODE', 'I have a coupon code')}
                                </Label>
                            </div>

                            {couponEnabled && (
                                <div className="space-y-2">
                                    {appliedQuote ? (
                                        <div className="flex items-center justify-between gap-3 rounded-xl border border-[#d1d1d1] bg-[#f7f7f5] px-4 py-3 dark:border-[#3d3d3d] dark:bg-[#19191c]">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1a7f37]/10 dark:bg-[#4ade80]/10">
                                                    <Check width={12} height={12} strokeWidth={2.5} className="text-[#1a7f37] dark:text-[#4ade80]" />
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-[#171b23] dark:text-foreground">
                                                        {appliedCoupon?.code.toUpperCase()}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {t('COUPON_APPLIED', 'Coupon applied')}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleRemoveCoupon}
                                                aria-label={t('REMOVE_COUPON', 'Remove coupon')}
                                                className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-[#e7e7e7] hover:text-foreground dark:hover:bg-[#2a2a2e]"
                                            >
                                                <Xmark width={16} height={16} strokeWidth={2} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-start gap-2">
                                            <Input
                                                value={couponCode}
                                                onChange={(e) => {
                                                    setCouponCode(e.target.value);
                                                    if (couponError) setCouponError(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleApplyCoupon();
                                                    }
                                                }}
                                                placeholder={t('COUPON_CODE_PLACEHOLDER', 'Enter coupon code')}
                                                disabled={isPreviewingDiscount}
                                                className="h-11 flex-1 rounded-xl border-[#d1d1d1] uppercase placeholder:normal-case dark:border-[#3d3d3d]"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={handleApplyCoupon}
                                                disabled={!couponCode.trim() || isPreviewingDiscount}
                                                loading={isPreviewingDiscount}
                                                className="h-11 shrink-0 rounded-xl px-5"
                                            >
                                                {t('APPLY', 'Apply')}
                                            </Button>
                                        </div>
                                    )}
                                    {couponError && (
                                        <p className="text-xs text-destructive">{couponError}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-[#e7e7e7] pt-4 dark:border-border">
                            <div className="flex justify-between text-sm text-muted-foreground">
                                <span>{t('SUBTOTAL', 'Subtotal')}</span>
                                <span>{formatMoney(displaySubtotal)}</span>
                            </div>
                            {appliedQuote && discountAmount > 0 && (
                                <div className="mt-2 flex justify-between text-sm text-[#1a7f37] dark:text-[#4ade80]">
                                    <span>
                                        {t('COUPON_DISCOUNT', 'Discount')} ({appliedCoupon?.code.toUpperCase()})
                                    </span>
                                    <span>-{formatMoney(discountAmount)}</span>
                                </div>
                            )}
                            <div className="mt-4 flex justify-between text-sm font-semibold text-[#171b23] dark:text-foreground">
                                <span>{t('TOTAL_DUE', 'Total due')}</span>
                                <span>{formatMoney(totalDue)}</span>
                            </div>
                            {appliedQuote && appliedQuote.nextRenewalAmountMinor !== appliedQuote.totalDueNowMinor && (
                                <p className="mt-2 text-xs text-muted-foreground">
                                    {t('THEN_RENEWS_AT', 'Then {{amount}} on {{date}}', {
                                        amount: formatMoney(appliedQuote.nextRenewalAmountMinor / 100),
                                        date: new Date(appliedQuote.nextRenewalAtUtc).toLocaleDateString(),
                                    })}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-muted-foreground">
                                {t('TAX_CALCULATED_AT_CHECKOUT', 'Tax will be calculated at checkout')}
                            </p>
                        </div>
                    </div>

                    {plan.entitlements.length > 0 && (
                        <div className="mt-10">
                            <p className="mb-3.5 text-[10.5px] uppercase tracking-[0.1em] text-[#565f6b] dark:text-muted-foreground">
                                {t('WHATS_INCLUDED', "What's included")}
                            </p>
                            <ul className="flex flex-col gap-[9px]">
                                {plan.entitlements.map((entitlement) => (
                                    <li
                                        key={entitlement.key}
                                        className="flex items-center gap-2 text-[13.5px] text-[#171b23] dark:text-foreground"
                                    >
                                        <Check width={13} height={13} strokeWidth={2} className="shrink-0" />
                                        <span>
                                            {formatLabel(entitlement.key)}
                                            {entitlement.limitKind === 'Count' && entitlement.limit != null && ` (${entitlement.limit.toLocaleString()})`}
                                            {entitlement.limitKind === 'Unlimited' && ` · ${t('UNLIMITED', 'Unlimited')}`}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {plan.meters.length > 0 && (
                        <div className="mt-8">
                            <p className="mb-3.5 text-[10.5px] uppercase tracking-[0.1em] text-[#565f6b] dark:text-muted-foreground">
                                {t('USAGE_METERS', 'Usage meters')}
                            </p>
                            <ul className="divide-y divide-[#e7e7e7] dark:divide-border">
                                {plan.meters.map((meter) => (
                                    <li key={meter.meterKey} className="flex items-baseline justify-between gap-3 py-3 first:pt-0">
                                        <span className="text-[13.5px] font-medium text-[#171b23] dark:text-foreground">
                                            {meter.displayName}
                                        </span>
                                        <span className="text-sm font-semibold text-[#171b23] dark:text-foreground">
                                            {meter.includedQuantity.toLocaleString()}
                                            <span className="text-[10.5px] font-normal text-[#565f6b] dark:text-muted-foreground">
                                                {' '}
                                                {meter.unitLabel}/mo
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </aside>

            <main className="flex items-start justify-center bg-white px-6 py-8 dark:bg-background lg:px-10 lg:py-10">
                <div className="w-full max-w-[600px]">
                    <h2 className="mb-6 text-base font-semibold leading-[1.2] text-grey-700 dark:text-foreground">
                        {t('BILLING_PROFILE', 'Billing profile')}
                    </h2>

                    <Form {...form}>
                        <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-8">
                            <div className="grid w-full grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                                <FormField
                                    control={control}
                                    name="legalName"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('LEGAL_NAME', 'Legal name')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="organization"
                                                    placeholder="Northwind Trading AG"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormDescription className={hintClassName}>
                                                {t(
                                                    'LEGAL_NAME_HINT',
                                                    'The name the organization contracts under. This is what a document has to carry.'
                                                )}
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="displayName"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('DISPLAY_NAME', 'Display name')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    placeholder="Northwind"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormDescription className={hintClassName}>
                                                {t('DISPLAY_NAME_HINT', 'Optional. Falls back to the legal name.')}
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="billingContactName"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('BILLING_CONTACT', 'Billing contact')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="name"
                                                    placeholder="Ada Byron"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="billingContactEmail"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('BILLING_EMAIL', 'Billing email')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="email"
                                                    autoComplete="email"
                                                    placeholder="billing@northwind.example"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormDescription className={hintClassName}>
                                                {t(
                                                    'BILLING_EMAIL_HINT',
                                                    'Where invoices, trial invoices and credit notes are emailed.'
                                                )}
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="space-y-2.5 sm:col-span-2">
                                    <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                        {t('ADDRESS', 'Address')}
                                    </FormLabel>
                                    <FormField
                                        control={control}
                                        name="line1"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2.5">
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        type="text"
                                                        autoComplete="address-line1"
                                                        placeholder="1 Bahnhofstrasse"
                                                        className={klaxInputClassName}
                                                        style={{ height: 54 }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={control}
                                        name="line2"
                                        render={({ field }) => (
                                            <FormItem className="space-y-2.5">
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        type="text"
                                                        autoComplete="address-line2"
                                                        placeholder={t(
                                                            'ADDRESS_LINE_2_PLACEHOLDER',
                                                            'Second line, if there is one'
                                                        )}
                                                        className={klaxInputClassName}
                                                        style={{ height: 54 }}
                                                    />
                                                </FormControl>
                                                <FormDescription className={hintClassName}>
                                                    {t(
                                                        'ADDRESS_HINT',
                                                        'Optional — many subscribers have no address to state, and a subscription is never refused for the want of one.'
                                                    )}
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <FormField
                                    control={control}
                                    name="postalCode"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('POSTAL_CODE', 'Postal code')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="postal-code"
                                                    placeholder="8001"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="city"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('CITY', 'City')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="address-level2"
                                                    placeholder="Zurich"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="region"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('STATE_OR_REGION', 'State or region')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="address-level1"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="countryCode"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('COUNTRY_CODE', 'Country code')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    autoComplete="country"
                                                    maxLength={2}
                                                    placeholder="CH"
                                                    className={cn(klaxInputClassName, 'uppercase')}
                                                    style={{ height: 54 }}
                                                    onChange={(event) =>
                                                        field.onChange(event.target.value.toUpperCase())
                                                    }
                                                />
                                            </FormControl>
                                            <FormDescription className={hintClassName}>
                                                {t('COUNTRY_CODE_HINT', 'Two letters, ISO 3166-1.')}
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={control}
                                    name="taxRegistrationId"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2.5 sm:col-span-2">
                                            <FormLabel className="text-base font-medium leading-[1.2] text-black dark:text-true-white">
                                                {t('TAX_OR_VAT_REGISTRATION', 'Tax or VAT registration')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="text"
                                                    placeholder="CHE-123.456.789"
                                                    className={klaxInputClassName}
                                                    style={{ height: 54 }}
                                                />
                                            </FormControl>
                                            <FormDescription className={hintClassName}>
                                                {t(
                                                    'TAX_REGISTRATION_HINT',
                                                    'Printed exactly as entered. Every jurisdiction spells these differently, so nothing here reformats it.'
                                                )}
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <Button
                                type="submit"
                                variant="outline"
                                className={cn(
                                    'flex h-[48px] w-full items-center justify-center gap-2 rounded-full px-10 py-3 text-sm font-medium leading-[1.2] shadow-none sm:gap-[12px]',
                                    isBillingIncomplete
                                        ? 'border-[#D1D1D1] bg-[#D1D1D1] text-[#6D6D6D] hover:bg-[#D1D1D1] hover:text-[#6D6D6D] disabled:opacity-100 dark:border-[#6D6D6D] dark:bg-[#6D6D6D] dark:text-[#D1D1D1] dark:hover:bg-[#6D6D6D] dark:hover:text-[#D1D1D1]'
                                        : 'border-black bg-black text-base font-medium leading-[1.2] text-[#ffffff] hover:bg-black hover:text-[#ffffff] disabled:opacity-100 dark:border-[#ffffff] dark:bg-[#ffffff] dark:text-black dark:hover:bg-[#ffffff] dark:hover:text-black'
                                )}
                                disabled={isBusy || isBillingIncomplete}
                                loading={isPending || isUpdatingBillingProfile}
                            >
                                {isPending || isUpdatingBillingProfile
                                    ? t('PROCESSING', 'Processing...')
                                    : `${t('PAY', 'Pay')} ${formatMoney(totalDue)}`}
                            </Button>
                        </form>
                    </Form>
                </div>
            </main>
        </div>
    );
}
